/**
 * PromptStudioView — the Prompts tab body.
 *
 * Presentational only. Receives BrandQuery[], AllowanceFigures, lastRuns as
 * props. Renders the prompt list grouped by listGroups. Uses studioReducer for
 * local state. Testable by SSR.
 *
 * @see B-popup-frontend.md step 4, Files and props § PromptStudioView
 */
import { useReducer, useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { BrandQuery, Brand, Tenant, Location, BrandQueryAxis, FirstRunNotice, TargetZone } from '@traaker/shared';
import type { PromptLastRun } from '@traaker/shared';
import { promptPlaceKey, zoneDisplayLabel } from '@traaker/shared';
import {
  groupFamilies,
  AXIS_SPLIT_LIVE,
  offeredAxes,
  knownZones,
  promptPickerZones,
  MONDIAL,
  toggleAllVisible,
} from './promptStudioModel';
import type { LocKey } from './promptStudioModel';
import { Pick } from './Pick';
import { brandQueriesApi } from '../../services/brand-queries';
import { PromptStudioComposer, SOURCE_LABELS } from './PromptStudioComposer';
import type { ComposerState } from './PromptStudioComposer';
import { ReviewTable } from './ReviewTable';
import type { ReviewRow } from './studioReducer';
import { classifyDraft, classifyRequest, seedFromCell, toReviewRow, SOURCE_TO_BULK } from './composerModel';
import type { ComposerSource } from './composerModel';
import {
  EMPTY_FILTER,
  PENDING_ONLY,
  withTag,
  applyFilters as applyFiltersModel,
  groupRows,
} from './filterModel';
import type { FilterState } from './filterModel';
import { zonesAsRows, coverageLocs, nVocabMax, vocabFor } from './coverageModel';
import type { CellTriple } from './promptStudioModel';
import {
  studioReducer,
  initialStudioState,
} from './studioReducer';
import type { StudioTab } from './studioReducer';
import { PromptList, BatchBar } from './PromptList';
import { FiltersPanel } from './FiltersPanel';
import { CoverageTab } from './CoverageTab';
import { FirstRunBanner } from './FirstRunBanner';
import { FooterStrip } from './FooterStrip';
import type { PromptAllowanceFigures } from './promptStudioModel';
import { promptBarNums } from './FooterSettingsPanel';
import type { AgencyFooterFigures, FooterChange } from './FooterSettingsPanel';
import type { CovConversation } from './coverageModel';
import type { AllowanceFigures } from '@traaker/shared';
import type { ListGroupKey } from './promptStudioModel';
import { ConversationsTab, EMPTY_CONV_DATA } from './conversations/ConversationsTab';
import type { ConvData } from './conversations/ConversationsTab';
import { EMPTY_CONV_FILTER, conversationZones, covConversations, convFilterFromCell } from './conversations/conversationModel';
import type { ConvFilter } from './conversations/conversationModel';
import type { ConversationLoad, ConversationPersona } from '@traaker/shared';
import { ConversationsFooterPanel, conversationBarNums } from './conversations/ConversationsFooterPanel';

export interface PromptStudioViewProps {
  tab: StudioTab;
  brand: Brand;
  tenant?: Tenant;
  queries: BrandQuery[];
  loadingQueries: boolean;
  errorQueries: boolean;
  promptFigures: PromptAllowanceFigures | null;
  conversationFigures: AllowanceFigures | null;
  runs: Record<string, PromptLastRun> | null;
  initialAxis: BrandQueryAxis;
  q: string;
  onQ: (q: string) => void;
  filter: FilterState;
  onFilter: (filter: FilterState) => void;
  filtersOpen: boolean;
  onCloseFilters: () => void;
  onChanged: () => void;
  onAddConversation?: () => void;
  onTabChange?: (tab: StudioTab) => void;
  agencyFigures?: AgencyFooterFigures;
  onLaunch?: () => void;
  /** A footer cadence / engines write succeeded (FooterSettingsPanel). */
  onFooterSaved?: (change: FooterChange) => void;
  initialFooterOpen?: boolean;
  /** When true, the footer Conversations popover (the conversation cadence) opens on mount. */
  initialConvFooterOpen?: boolean;
  promptMargin?: { lo: number; hi: number } | null;
  /** Open on the composer with this source (the Search Console OAuth return, lot H). */
  initialSource?: ComposerSource;
  /** Search Console (lot H): isAgency → the band's agency line. */
  searchConsole?: { isAgency: boolean };
  /** Lot I3: the Conversations tab is shown (flag AND `conversationRuns.allowance !== 0`). */
  conversationsVisible?: boolean;
  /** 'new' opens the new-conversation form, a personaId that conversation's form. */
  initialConversation?: string;
  conv?: ConvData;
  convFilter?: ConvFilter;
  onConvFilter?: (f: ConvFilter) => void;
  onConvRetry?: () => void;
  onConvSaved?: (persona: ConversationPersona, load: ConversationLoad) => void;
  onConvRemoved?: (personaId: string) => void;
  onConvLoad?: (load: ConversationLoad) => void;
  onManagePersonas?: () => void;
}

export function PromptStudioView({
  tab,
  brand,
  tenant: _tenant,
  queries,
  loadingQueries,
  errorQueries,
  promptFigures,
  conversationFigures,
  runs,
  initialAxis,
  q,
  onQ,
  filter,
  onFilter,
  filtersOpen,
  onCloseFilters,
  onChanged,
  onAddConversation: _onAddConversation,
  onTabChange,
  agencyFigures,
  onLaunch,
  onFooterSaved,
  initialFooterOpen,
  initialConvFooterOpen,
  initialSource,
  searchConsole,
  conversationsVisible = false,
  initialConversation,
  conv = EMPTY_CONV_DATA,
  convFilter = EMPTY_CONV_FILTER,
  onConvFilter = () => {},
  onConvRetry = () => {},
  onConvSaved = () => {},
  onConvRemoved = () => {},
  onConvLoad = () => {},
  onManagePersonas = () => {},
}: PromptStudioViewProps) {
  const { t, i18n } = useTranslation();
  const [state, dispatch] = useReducer(studioReducer, tab, (tb: StudioTab) =>
    initialSource ? { ...initialStudioState(tb), body: 'composer' as const } : initialStudioState(tb),
  );

  const locations: Location[] = (brand as any).locations ?? [];
  const strategy = brand.strategy;
  const isAgency = _tenant?.tier === 'agency';
  const workingLanguage = (strategy?.workingLanguage || 'fr').toLowerCase();
  const genLanguages = useMemo(
    () =>
      [...new Set([workingLanguage, ...(strategy?.targetLanguages ?? []).map((l) => l.trim().toLowerCase()).filter(Boolean)])]
        .map((code) => ({ code, label: languageLabel(code, i18n.language) })),
    [workingLanguage, strategy?.targetLanguages, i18n.language],
  );

  // Composer + review (body 'composer' / 'review' of the Prompts tab)
  const axes = offeredAxes(AXIS_SPLIT_LIVE);
  const genAxis = axes.includes(initialAxis) ? initialAxis : axes[0];
  const [composer, setComposer] = useState<ComposerState>(() => ({
    ...emptyComposer(genAxis, workingLanguage),
    ...(initialSource ? { source: initialSource } : {}),
  }));
  const [review, setReview] = useState<{ rows: ReviewRow[]; source: ComposerSource; rateLimited: number } | null>(null);
  const reviewRowsRef = useRef<ReviewRow[]>([]);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const vocabByAxis = useMemo(
    () => ({
      narrative: vocabFor(strategy, 'narrative'),
      authority: vocabFor(strategy, 'authority'),
      referral: vocabFor(strategy, 'referral'),
      comparison: vocabFor(strategy, 'comparison'),
    }),
    [strategy],
  );

  const openComposer = useCallback(() => {
    setVerifyError(null);
    dispatch({ type: 'setBody', body: 'composer' });
    if (tab !== 'prompts') onTabChange?.('prompts');
  }, [tab, onTabChange]);

  const handleVerify = async (lines: string[], source: ComposerSource) => {
    if (verifying) return;
    setVerifying(true);
    setVerifyError(null);
    const res = await classifyDraft(lines, async (texts) => {
      const r = await brandQueriesApi.bulk(brand.brandId, classifyRequest(texts, source, composer.zone));
      return r.dryRun ? r.items : [];
    });
    setVerifying(false);
    if ('error' in res) {
      setVerifyError(
        res.error === 'rate_limited'
          ? t('promptStudio.composer.rateLimited')
          : 'Vérification impossible, réessayez.', // i18n: promptStudio.composer.verifyFailed
      );
      return;
    }
    reviewRowsRef.current = res.rows;
    setReview({ rows: res.rows, source, rateLimited: res.rateLimited });
    dispatch({ type: 'setBody', body: 'review' });
  };

  const handleReviewChange = (rows: ReviewRow[]) => {
    reviewRowsRef.current = rows;
    setReview((r) => r && { ...r, rows, rateLimited: Math.min(r.rateLimited, rows.filter((x) => !x.intent).length) });
  };

  const handleRetry = async (row: ReviewRow) => {
    if (!review) return;
    const rowZone = row.zoneMeta !== undefined ? (row.zoneMeta as TargetZone | null) : composer.zone;
    try {
      const r = await brandQueriesApi.bulk(brand.brandId, classifyRequest([row.text], review.source, rowZone));
      const item = r.dryRun ? r.items[0] : undefined;
      if (item) {
        // The retried row keeps its own place.
        const next: ReviewRow = { ...toReviewRow(item), ...(row.zoneMeta !== undefined ? { zoneMeta: row.zoneMeta } : {}) };
        handleReviewChange(reviewRowsRef.current.map((x) => (x === row ? next : x)));
      }
    } catch (err) {
      // A 429 keeps the row and shows the rate-limit line.
      if ((err as { status?: number })?.status === 429) {
        setReview((r) => r && { ...r, rateLimited: Math.max(r.rateLimited, 1) });
      }
    }
  };

  const handleCreated = (_created: BrandQuery[], firstRun?: FirstRunNotice[]) => {
    dispatch({ type: 'firstRunSaved', firstRun });
    onChanged();
    // Rows the server refused stay in the review; a clean save goes back to the list.
    if (reviewRowsRef.current.length === 0) {
      setReview(null);
      setComposer((c) => ({ ...emptyComposer(genAxis, c.gen.language), cooldownUntil: c.cooldownUntil }));
      dispatch({ type: 'setBody', body: 'list' });
    }
  };

  // Families + groups
  const families = useMemo(() => groupFamilies(queries), [queries]);

  // Apply filters and group
  const filteredFamilies = useMemo(
    () => applyFiltersModel(families, q, filter, locations, strategy),
    [families, q, filter, locations, strategy],
  );

  const groupedRows = useMemo(
    () => groupRows(filteredFamilies, filter.groupBy, locations, strategy).map((g) => ({
      key: g.key as ListGroupKey,
      families: g.families,
      label: g.label,
      axis: g.axis,
    })),
    [filteredFamilies, filter, locations, strategy],
  );

  // Batch selection (family root ids). Only the listed active families count: a
  // filter, a search or a reload that hides a row takes it out of the batch.
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const selectedFamilies = useMemo(
    () => filteredFamilies.filter((f) => f.root.status === 'active' && selected.has(f.root.queryId)),
    [filteredFamilies, selected],
  );
  const toggleSelected = useCallback((id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);
  // The header checkbox acts on the rows on screen (the same set the row checkboxes show).
  const visibleSelectable = useMemo(
    () => filteredFamilies.filter((f) => f.root.status === 'active').map((f) => f.root.queryId),
    [filteredFamilies],
  );
  const nVisibleSelected = visibleSelectable.filter((id) => selected.has(id)).length;

  // Couverture « + Ajouter une localisation » (§2.18): empty session-only lanes,
  // nothing saved, lost on close (the popup unmounts this view).
  const [sessionZones, setSessionZones] = useState<TargetZone[]>([]);
  const [addLocOpen, setAddLocOpen] = useState(false);
  const [addLocQuery, setAddLocQuery] = useState('');
  const [laneFocus, setLaneFocus] = useState<{ key: LocKey } | null>(null);

  // Coverage data
  const [covAxis, setCovAxis] = useSimpleState<BrandQueryAxis>(initialAxis);
  // The session lanes are known places too (C14: the Couverture rows), so their
  // hatched cells seed the composer with the full zone and every picker lists them.
  // C14: the brand's conversation places are known places too.
  const convZones = useMemo(() => conversationZones(conv.personas), [conv.personas]);
  const known = useMemo(
    () => knownZones(queries, [...sessionZones, ...convZones], locations),
    [queries, sessionZones, convZones, locations],
  );
  // « + Nouvelle conversation » (footer, empty list): the form replaces the list.
  const [convCreating, setConvCreating] = useState(initialConversation === 'new');
  const placeZones = useMemo(() => promptPickerZones(known, locations), [known, locations]);
  const bulkDefaults = useMemo(() => ({ zoneMeta: composer.zone }), [composer.zone]);
  // Couverture counts conversations only while their tab is shown (I-D1): else B gets [] and no 🤖.
  const convs: CovConversation[] = useMemo(
    () => (conversationsVisible
      ? covConversations(conv.personas, new Map(queries.map((r) => [r.queryId, r])), vocabByAxis, known)
      : []),
    [conversationsVisible, conv.personas, queries, vocabByAxis, known],
  );
  // The lanes: the prompts' places, then the conversations' (a key with no lane would be dropped).
  const dataLocs = useMemo(() => {
    const locs = coverageLocs(families, [], locations, known);
    for (const c of convs) for (const k of c.locs) if (!locs.includes(k)) locs.push(k);
    return locs;
  }, [families, locations, known, convs]);
  const covLocs = useMemo(
    () => [...dataLocs, ...sessionZones.map(promptPlaceKey).filter((k) => !dataLocs.includes(k))],
    [dataLocs, sessionZones],
  );
  // From the data's places only: a session lane never flips the orientation (§6.4).
  const zr = useMemo(
    () => zonesAsRows(dataLocs.length, nVocabMax(strategy)),
    [dataLocs.length, strategy],
  );

  // A pick: an existing lane is only scrolled to; a new place adds an empty lane.
  const addLane = (zone: TargetZone | null) => {
    setAddLocOpen(false);
    const key = zone ? promptPlaceKey(zone) : MONDIAL;
    if (zone && !covLocs.includes(key)) setSessionZones((zs) => [...zs, zone]);
    setLaneFocus({ key });
  };
  useEffect(() => {
    if (!laneFocus) return;
    // CoverageTab marks each place label with its index in `locs`.
    document
      .querySelector(`[data-cov-lane="${covLocs.indexOf(laneFocus.key)}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [laneFocus]); // eslint-disable-line react-hooks/exhaustive-deps


  // Pending count
  const pendingCount = families.filter((f) => f.root.axis === 'pending').length;

  // Measured coverage cell → filter to that cell and switch to prompts tab
  const handleCovFilter = useCallback((next: FilterState, _ax: BrandQueryAxis) => {
    onFilter(next);
    dispatch({ type: 'setTab', tab: 'prompts' });
    onTabChange?.('prompts');
  }, [onFilter, onTabChange]);

  // Hatched cell → the Générer source seeded with the cell, its place the batch place; nothing runs before « Générer ».
  const handleCovCell = useCallback((cell: CellTriple) => {
    const zone = cell.loc === MONDIAL ? null : known.get(cell.loc) ?? null;
    setComposer((c) => ({ ...c, source: 'gen', gen: seedFromCell(cell, c.gen.language), genDone: false, zone }));
    setVerifyError(null);
    dispatch({ type: 'setBody', body: 'composer' });
    dispatch({ type: 'setTab', tab: 'prompts' });
    onTabChange?.('prompts');
  }, [onTabChange, known]);

  const handleTag = useCallback((tag: string) => {
    onFilter(withTag(filter, tag));
    dispatch({ type: 'setOpenId', id: null });
    onQ('');
    dispatch({ type: 'setTab', tab: 'prompts' });
  }, [filter, onFilter, onQ]);

  if (loadingQueries) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Skeleton header */}
        <Box sx={{ display: 'flex', gap: 0, height: 36, borderBottom: '1px solid', borderColor: 'divider' }}>
          {[140, 80, 80].map((w, i) => (
            <Box key={i} sx={{ width: w, m: 1, height: 20, bgcolor: 'rgba(0,0,0,.06)', borderRadius: 1 }} />
          ))}
        </Box>
        {/* 3 skeleton rows */}
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{ height: 46, mx: 2, my: 0.5, bgcolor: 'rgba(0,0,0,.04)', borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (errorQueries) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
          {/* i18n: promptStudio.list.loadFailed */}
          Impossible de charger les prompts.
        </Box>
        <Button size="small" onClick={onChanged}>
          {/* i18n: common.retry */}
          Réessayer
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* FirstRunBanner */}
      {state.firstRunNotice && (
        <FirstRunBanner
          entries={state.firstRunNotice}
          onClose={() => dispatch({ type: 'dismissFirstRun' })}
        />
      )}

      {tab === 'prompts' && state.body === 'composer' && (
        <>
          {(verifyError || verifying) && (
            <Box sx={{ px: 2, py: 0.75, fontSize: 12, flexShrink: 0, bgcolor: verifyError ? '#fffbeb' : 'transparent', color: verifyError ? '#b45309' : 'text.secondary' }}>
              {verifyError ?? 'Vérification…' /* i18n: promptStudio.composer.verifying */}
            </Box>
          )}
          <PromptStudioComposer
            state={composer}
            brandId={brand.brandId}
            axes={axes}
            vocabByAxis={vocabByAxis}
            languages={genLanguages}
            // Brand Knowledge personas (loaded with the conversations flag): the Générer « Persona » chip.
            personas={conv.loaded ? conv.brandPersonas : null}
            // Agency has no quota margin to grey on (lot G's balance applies instead).
            figures={isAgency ? null : promptFigures}
            locations={locations}
            placeZones={placeZones}
            lang={workingLanguage}
            onChange={(next) => setComposer((c) => ({ ...c, ...next }))}
            onVerifier={(lines, source) => void handleVerify(lines, source)}
            onCancel={() => dispatch({ type: 'setBody', body: 'list' })}
            searchConsole={searchConsole && { ...searchConsole, brandUrl: brand.url }}
          />
        </>
      )}

      {tab === 'prompts' && state.body === 'review' && review && (
        <ReviewTable
          rows={review.rows}
          source={SOURCE_LABELS[review.source]}
          bulkSource={SOURCE_TO_BULK[review.source]}
          bulkDefaults={bulkDefaults}
          placeZones={placeZones}
          lang={workingLanguage}
          mode="batch"
          brandId={brand.brandId}
          isAgency={isAgency}
          currentFigures={promptFigures}
          monthFigure={null}
          rateLimited={review.rateLimited}
          firstRun={null}
          onChange={handleReviewChange}
          onRetry={(row) => void handleRetry(row)}
          onBack={() => {
            setReview(null);
            dispatch({ type: 'setBody', body: 'composer' });
          }}
          onCreated={handleCreated}
          criteriaOptions={strategy?.decisionCriteria ?? []}
          topicOptions={{ authority: strategy?.keywords ?? [], referral: strategy?.scenarios ?? [] }}
        />
      )}

      {tab === 'prompts' && state.body === 'list' && (
        <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {selectedFamilies.length > 0 && (
            <BatchBar
              families={selectedFamilies}
              brandId={brand.brandId}
              onDone={(failed) => {
                setSelected(new Set(failed));
                onChanged();
              }}
              onClear={() => setSelected(new Set())}
            />
          )}
          <ColumnHeader
            selectAll={{
              checked: visibleSelectable.length > 0 && nVisibleSelected === visibleSelectable.length,
              indeterminate: nVisibleSelected > 0 && nVisibleSelected < visibleSelectable.length,
              disabled: visibleSelectable.length === 0,
              onToggle: () => setSelected((s) => toggleAllVisible(s, visibleSelectable)),
            }}
          />
          <PromptList
            groups={groupedRows}
            openId={state.openId}
            q={q}
            runs={runs}
            locations={locations}
            lang={workingLanguage}
            strategy={strategy}
            zoneFilter={filter.facets.zone}
            tier={_tenant?.tier}
            promptFigures={promptFigures}
            brandId={brand.brandId}
            onChanged={onChanged}
            onFirstRun={(firstRun) => dispatch({ type: 'firstRunSaved', firstRun })}
            onOpen={(id) => dispatch({ type: 'setOpenId', id })}
            onTag={handleTag}
            onDisable={(f) => dispatch({ type: 'disable', queryId: f.root.queryId, axis: f.root.axis })}
            onDelete={(f) => dispatch({ type: 'confirmDelete', queryId: f.root.queryId, axis: f.root.axis })}
            onRetryClassify={(queryId) =>
              dispatch({ type: 'submitEdit', queryId, body: { axis: 'pending' } })
            }
            onReactivate={(f) =>
              dispatch({ type: 'reactivate', queryId: f.root.queryId, axis: f.root.axis })
            }
            hasFilter={Object.values(filter.facets).some((v) => v && v.length > 0) || !!q}
            onClearFilter={() => {
              onFilter(EMPTY_FILTER);
              onQ('');
            }}
            onAdd={openComposer}
            selected={selected}
            onSelect={toggleSelected}
          />
          {filtersOpen && (
            <FiltersPanel
              state={filter}
              onChange={onFilter}
              families={families}
              brand={brand}
              hasMultipleBrands={false}
              q={q}
              onClose={onCloseFilters}
            />
          )}
        </Box>
      )}

      {tab === 'coverage' && (
        <CoverageTab
          families={families}
          convs={convs}
          ax={covAxis}
          axes={axes}
          onAxis={setCovAxis}
          zr={zr}
          locs={covLocs}
          locations={locations}
          known={known}
          strategy={strategy}
          onFilter={handleCovFilter}
          onCell={handleCovCell}
          onConvCell={(cell) => {
            onConvFilter(convFilterFromCell(cell));
            onQ('');
            onTabChange?.('conversations');
          }}
          onPending={() => {
            onFilter(PENDING_ONLY);
            dispatch({ type: 'setTab', tab: 'prompts' });
            onTabChange?.('prompts');
          }}
          onUnclassified={() => {
            onFilter({ ...EMPTY_FILTER, facets: { status: ['?'], axis: [covAxis] } });
            dispatch({ type: 'setTab', tab: 'prompts' });
            onTabChange?.('prompts');
          }}
          pendingCount={pendingCount}
          onAddLoc={() => { setAddLocQuery(''); setAddLocOpen(true); }}
        />
      )}
      <Pick
        open={tab === 'coverage' && addLocOpen}
        kind="place"
        selected=""
        onToggle={(key) => addLane(key === MONDIAL ? null : placeZones.get(key) ?? null)}
        onPickZone={addLane}
        onClose={() => setAddLocOpen(false)}
        placeOptions={[...placeZones].map(([key, z]) => ({ key, zone: z, label: zoneDisplayLabel(z) }))}
        searchQuery={addLocQuery}
        onSearchChange={setAddLocQuery}
        lang={workingLanguage}
      />

      {tab === 'conversations' && (
        <ConversationsTab
          brand={brand}
          tenant={_tenant}
          data={conv}
          queries={queries}
          known={known}
          vocabByAxis={vocabByAxis}
          q={q}
          filter={convFilter}
          onFilter={onConvFilter}
          filtersOpen={filtersOpen}
          onCloseFilters={onCloseFilters}
          creating={convCreating}
          onCreating={setConvCreating}
          initialOpenId={initialConversation !== 'new' ? initialConversation : undefined}
          onRetry={onConvRetry}
          onSaved={onConvSaved}
          onRemoved={onConvRemoved}
          onReload={onConvRetry}
          onManagePersonas={onManagePersonas}
        />
      )}

      {/* Footer */}
      <FooterStrip
        promptFigures={promptFigures}
        // C15: the conversation figures of `GET .../conversations/load` (the same object as the allowances').
        conversationFigures={conv.load?.figures ?? conversationFigures}
        promptNums={promptBarNums(promptFigures, isAgency, t)}
        conversationNums={conversationBarNums(conv.load, isAgency, t)}
        conversationCreditsPerMonth={conv.load?.brandCreditsPerMonth ?? undefined}
        conversationPanel={conv.load ? (
          <ConversationsFooterPanel brandId={brand.brandId} isAgency={isAgency} load={conv.load} onLoad={onConvLoad} />
        ) : undefined}
        tab={tab}
        showConversations={conversationsVisible}
        onAddPrompt={openComposer}
        onAddConversation={conversationsVisible ? () => setConvCreating(true) : undefined}
        brand={brand}
        tenant={_tenant}
        agencyFigures={agencyFigures}
        onLaunch={onLaunch}
        onFooterSaved={onFooterSaved}
        initialPopoverOpen={initialFooterOpen}
        initialConvPopoverOpen={initialConvFooterOpen}
      />
    </Box>
  );
}

export function languageLabel(code: string, uiLang: string): string {
  try {
    const name = new Intl.DisplayNames([uiLang || 'fr'], { type: 'language' }).of(code) ?? code;
    return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    return code;
  }
}

/** A fresh composer (also the onboarding « Vos prompts » step's). */
export function emptyComposer(axis: BrandQueryAxis, language: string): ComposerState {
  return {
    source: 'write',
    drafts: { write: '', gen: '', persona: '', kw: '', csv: '', gsc: '' },
    gen: { axis, terms: [], competitors: [], personaId: null, count: 5, loc: MONDIAL, language },
    cooldownUntil: null,
    genDone: false,
    kwDone: false,
    kwDropped: 0,
    keywords: [],
    zone: null,
  };
}

function ColumnHeader({
  selectAll,
}: {
  selectAll: { checked: boolean; indeterminate: boolean; disabled: boolean; onToggle: () => void };
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        bgcolor: '#fafbfd',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 0,
        py: 0.75,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '.04em',
        color: 'text.secondary',
        flexShrink: 0,
      }}
    >
      <Box sx={{ width: 6, flexShrink: 0 }} />
      <Box sx={{ flex: 1, px: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          component="input"
          type="checkbox"
          checked={selectAll.checked}
          disabled={selectAll.disabled}
          ref={(el: HTMLInputElement | null) => {
            if (el) el.indeterminate = selectAll.indeterminate;
          }}
          onChange={selectAll.onToggle}
          aria-label="Tout sélectionner" // i18n: promptStudio.batch.selectAll
          title="Sélectionner les prompts affichés" // i18n: promptStudio.batch.selectAllTitle
          sx={{ width: 12, height: 12, m: 0, flexShrink: 0, cursor: 'pointer', accentColor: '#7c3aed' }}
        />
        {/* i18n: promptStudio.col.question */}
        Question
      </Box>
      <Box sx={{ width: 168, minWidth: 168, flexShrink: 0, px: 0.5 }}>
        {/* i18n: promptStudio.col.place */}
        Lieu
      </Box>
      <Box sx={{ width: 68, minWidth: 68, flexShrink: 0, px: 0.5 }}>
        {/* i18n: promptStudio.col.languages */}
        Langues
      </Box>
      <Box sx={{ width: 72, minWidth: 72, textAlign: 'right', pr: 0.5, flexShrink: 0 }}>
        {/* i18n: promptStudio.col.lastRun */}
        Passage
      </Box>
      <Box sx={{ width: 60, minWidth: 60, textAlign: 'right', pr: 0.5, flexShrink: 0 }}>
        {/* i18n: promptStudio.col.prompts */}
        Prompts
      </Box>
      <Box sx={{ width: 18, flexShrink: 0 }} />
    </Box>
  );
}

/** Minimal useState-like for derived value that can also be set externally */
function useSimpleState<T>(initial: T): [T, (v: T) => void] {
  const [state, setState] = useReducer((_: T, v: T) => v, initial);
  return [state, setState];
}
